import { useState, useMemo } from 'react';
import { Calendar, Download, Search, Clock } from 'lucide-react';
import { Card } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Spinner } from '@/shared/components/ui/Spinner';
import { useAllTimeLogs } from '../hooks/useAttendance';

export function OverallAttendanceHistory({ isPrivileged }: { isPrivileged: boolean }) {
  const todayIso = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [dept, setDept] = useState('all');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const { data: logs = [], isLoading, isError } = useAllTimeLogs({
    dept: dept !== 'all' ? dept : undefined,
    from: from || undefined,
    to: to || undefined,
    limit: 500,
  }, isPrivileged);

  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      if (search) {
        const q = search.toLowerCase();
        const matchesName = (log.display_name || '').toLowerCase().includes(q);
        const matchesDept = (log.department || '').toLowerCase().includes(q);
        if (!matchesName && !matchesDept) return false;
      }
      if (statusFilter !== 'all') {
        const isOut = Boolean(log.check_out);
        const isIn = Boolean(log.check_in);
        if (statusFilter === 'checked_out' && !isOut) return false;
        if (statusFilter === 'checked_in' && (!isIn || isOut)) return false;
      }
      return true;
    });
  }, [logs, search, statusFilter]);

  const stats = useMemo(() => {
    let totalHours = 0;
    const employees = new Set<string>();
    for (const log of filteredLogs) {
      totalHours += Number(log.hours || 0);
      if (log.user_id) employees.add(String(log.user_id));
      else if (log.display_name) employees.add(log.display_name);
    }
    const avgHours = filteredLogs.length > 0 ? totalHours / filteredLogs.length : 0;
    return {
      totalLogs: filteredLogs.length,
      totalHours: Math.round(totalHours * 10) / 10,
      avgHours: Math.round(avgHours * 10) / 10,
      uniqueEmployees: employees.size,
    };
  }, [filteredLogs]);

  const handleQuickRange = (range: 'today' | 'week' | 'month' | 'all') => {
    const now = new Date();
    if (range === 'today') {
      setFrom(todayIso);
      setTo(todayIso);
    } else if (range === 'week') {
      const d = new Date(now);
      d.setDate(d.getDate() - 7);
      setFrom(d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }));
      setTo(todayIso);
    } else if (range === 'month') {
      const firstDay = `${todayIso.slice(0, 7)}-01`;
      setFrom(firstDay);
      setTo(todayIso);
    } else {
      setFrom('');
      setTo('');
    }
  };

  const exportCsv = () => {
    if (filteredLogs.length === 0) return;
    const headers = ['Date', 'Employee', 'Department', 'Role', 'Check In', 'Check Out', 'Hours', 'Status'];
    const rows = filteredLogs.map((l) => [
      l.log_date || '',
      `"${(l.display_name || '').replace(/"/g, '""')}"`,
      l.department || '',
      l.role || '',
      l.check_in || '',
      l.check_out || '',
      l.hours != null ? String(l.hours) : '0',
      l.check_out ? 'Checked Out' : l.check_in ? 'Checked In' : 'Absent',
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `zynx_overall_attendance_${todayIso}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Card className="rounded-xl border shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-border bg-slate-50/50 dark:bg-slate-900/50 px-5 py-4">
        <div>
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 undefined-undefined" />
            <h2 className="text-lg font-bold text-foreground">Overall Attendance & Daily History</h2>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Complete daily and historical records of employee check-ins, check-outs, and hours in ZYNX.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => handleQuickRange('today')} className="text-xs">
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleQuickRange('week')} className="text-xs">
            Past 7 Days
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleQuickRange('month')} className="text-xs">
            This Month
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleQuickRange('all')} className="text-xs">
            All Time
          </Button>
          <Button size="sm" onClick={exportCsv} disabled={filteredLogs.length === 0} className="bg-primary text-white text-xs gap-1.5 font-medium">
            <Download className="h-3.5 w-3.5" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="p-4 border-b border-border bg-white dark:bg-slate-950 flex flex-wrap items-center gap-3">
        <div className="relative w-60">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search employee..."
            className="w-full pl-8 text-xs"
          />
          <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>

        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Calendar className="h-3.5 w-3.5 undefined-undefined" />
          <span>From:</span>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="w-36 text-xs h-8"
          />
          <span>To:</span>
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="w-36 text-xs h-8"
          />
        </div>

        <Select
          value={dept}
          onChange={(e) => setDept(e.target.value)}
          options={[
            { value: 'all', label: 'All Departments' },
            { value: 'sales', label: 'Sales' },
            { value: 'accounts', label: 'Accounts' },
            { value: 'production', label: 'Production / Dispatch' },
            { value: 'management', label: 'Management' },
            { value: 'hr', label: 'HR' },
          ]}
          className="w-40 text-xs h-8"
        />

        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          options={[
            { value: 'all', label: 'All Statuses' },
            { value: 'checked_in', label: 'Currently In' },
            { value: 'checked_out', label: 'Checked Out' },
          ]}
          className="w-36 text-xs h-8"
        />
      </div>

      {/* KPI Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-slate-50/30 dark:bg-slate-900/30 border-b border-border">
        <div className="rounded-lg border border-border bg-white dark:bg-slate-950 p-3 text-center shadow-xs">
          <p className="text-2xl font-bold text-foreground">{stats.totalLogs}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Total Attendance Logs</p>
        </div>
        <div className="rounded-lg border border-border bg-white dark:bg-slate-950 p-3 text-center shadow-xs">
          <p className="text-2xl font-bold undefined-undefined undefined-undefined">{stats.uniqueEmployees}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Unique Employees</p>
        </div>
        <div className="rounded-lg border border-border bg-white dark:bg-slate-950 p-3 text-center shadow-xs">
          <p className="text-2xl font-bold undefined-undefined undefined-undefined">{stats.totalHours}h</p>
          <p className="text-xs text-muted-foreground mt-0.5">Total Hours Logged</p>
        </div>
        <div className="rounded-lg border border-border bg-white dark:bg-slate-950 p-3 text-center shadow-xs">
          <p className="text-2xl font-bold undefined-undefined undefined-undefined">{stats.avgHours}h</p>
          <p className="text-xs text-muted-foreground mt-0.5">Avg Hours / Session</p>
        </div>
      </div>

      {/* Table Content */}
      <div className="overflow-x-auto">
        {isLoading ? (
          <div className="flex h-36 items-center justify-center">
            <Spinner />
          </div>
        ) : isError ? (
          <div className="p-8 text-center text-sm undefined-undefined">
            Failed to load attendance records.
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <Clock className="h-10 w-10 mx-auto mb-2 opacity-30 undefined-undefined" />
            <p className="font-semibold text-foreground text-sm">No Attendance Logs Found</p>
            <p className="text-xs mt-1">Try adjusting the date range or department filter above.</p>
          </div>
        ) : (
          <table className="w-full text-left text-xs border-collapse">
            <thead className="bg-slate-100 dark:bg-slate-900 text-muted-foreground uppercase text-[11px] font-semibold border-b border-border sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Employee</th>
                <th className="px-4 py-3">Department</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Check In</th>
                <th className="px-4 py-3">Check Out</th>
                <th className="px-4 py-3 text-right">Hours</th>
                <th className="px-4 py-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {filteredLogs.map((l, idx) => {
                const isCheckedOut = Boolean(l.check_out);
                const isCheckedIn = Boolean(l.check_in);
                return (
                  <tr key={l.id || idx} className="hover:bg-slate-50/80 dark:hover:bg-slate-900/60 transition-colors">
                    <td className="px-4 py-3 font-mono font-medium text-foreground">
                      {l.log_date}
                    </td>
                    <td className="px-4 py-3 font-semibold text-foreground">
                      {l.display_name}
                    </td>
                    <td className="px-4 py-3 capitalize">
                      <span className="inline-block px-2 py-0.5 rounded text-[11px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                        {l.department || 'General'}
                      </span>
                    </td>
                    <td className="px-4 py-3 uppercase text-[10px] text-muted-foreground font-mono">
                      {l.role || '—'}
                    </td>
                    <td className="px-4 py-3 font-mono undefined-undefined undefined-undefined font-medium">
                      {l.check_in || '—'}
                    </td>
                    <td className="px-4 py-3 font-mono undefined-undefined undefined-undefined font-medium">
                      {l.check_out || '—'}
                    </td>
                    <td className="px-4 py-3 font-mono text-right font-semibold text-foreground">
                      {l.hours != null && l.hours > 0 ? `${l.hours}h` : '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isCheckedOut ? (
                        <Badge tone="info">Checked Out</Badge>
                      ) : isCheckedIn ? (
                        <Badge tone="success">Present</Badge>
                      ) : (
                        <Badge tone="neutral">Absent</Badge>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  );
}
